import React, { useState, useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { Button } from 'ui-components/components/button/';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Checkbox } from 'ui-components/components/checkbox/';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { CookieService } from '../../../../../services/cookie-service';
import { UserPermissionService } from '../../../../../services/user-permission-service';
import { emptyObject } from 'ui-components/default-props/empty';
import { MessageDescriptorPropType } from '../../../../../prop-types/intl';

import { generateWorkstationAuthToken, validateWorkstationAuthToken } from '../../../../../store/user/user.api';

import messages from './messages';
import styles from './styles.module.less';

const ONE_YEAR_IN_DAYS = 365;

const Restriction = ({ label, checked, disabled, onChange }) => {
  const CheckboxAndLabel = (
    <Checkbox
      checked={checked}
      className={styles.checkbox}
      disabled={disabled}
      label={<FormattedMessage {...label} />}
      onChange={onChange}
    />
  );

  if (!disabled) {
    return CheckboxAndLabel;
  }

  return (
    <Tooltip>
      <div>{CheckboxAndLabel}</div>
      <TooltipContent>
        <div>
          <FormattedMessage {...messages.cannotEditSelf} />
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

Restriction.propTypes = {
  label: MessageDescriptorPropType.isRequired,
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

const cookieKeyFromUsername = (username) => `WS_${username.toLowerCase()}`;

const Restrictions = ({
  username = '',
  updateForm,
  restrictions = emptyObject,
  isWorkstationApprovalRequired = false,
  disabled = false,
}) => {
  const [showLearnMore, setShowLearnMore] = useState(false);
  const toggleLearnMore = useCallback(() => {
    setShowLearnMore(!showLearnMore);
  }, [showLearnMore]);

  const [workstationApproved, setWorkstationApproved] = useAsyncSafeState(false);

  useEffect(() => {
    const token = CookieService.getCookieValue(cookieKeyFromUsername(username));
    if (!token) {
      return;
    }
    validateWorkstationAuthToken(username, token)
      .then((response) => {
        const { success } = response.data;
        setWorkstationApproved(success);
      })
      .catch();
  }, [username, setWorkstationApproved]);

  const generateToken = useCallback(() => {
    generateWorkstationAuthToken(username)
      .then((response) => {
        const token = response.data.token;
        CookieService.setCookieValueWithExpiration(cookieKeyFromUsername(username), token, ONE_YEAR_IN_DAYS);
        setWorkstationApproved(true);
      })
      .catch(() => {
        NotificationService.showNotification({
          title: '',
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.errorAuthorizing} />,
          duration: 3000,
        });
      });
  }, [username, setWorkstationApproved]);

  const onButtonClick = useCallback(() => {
    if (!workstationApproved) {
      generateToken();
      return;
    }
    CookieService.removeCookie(cookieKeyFromUsername(username));
    setWorkstationApproved(false);
  }, [workstationApproved, username, setWorkstationApproved, generateToken]);

  const workstationStatusLabel = workstationApproved ? (
    <FormattedMessage {...messages.workstationApproved} values={{ b: (msg) => <b>{msg}</b> }} />
  ) : (
    <FormattedMessage {...messages.workstationNotApproved} values={{ b: (msg) => <b>{msg}</b> }} />
  );

  const workstationApprovalButtonText = workstationApproved ? (
    <FormattedMessage {...messages.unapproveAccess} />
  ) : (
    <FormattedMessage {...messages.approveAccess} />
  );

  return (
    <div className={styles.restrictions}>
      <Restriction
        checked={restrictions[UserPermissionService.orderViewRestriction]}
        disabled={disabled}
        label={messages.orderRestriction}
        onChange={(value) => updateForm(UserPermissionService.orderViewRestriction, value)}
      />
      <Restriction
        checked={restrictions[UserPermissionService.shipmentViewRestriction]}
        disabled={disabled}
        label={messages.shipmentRestriction}
        onChange={(value) => updateForm(UserPermissionService.shipmentViewRestriction, value)}
      />
      <Restriction
        checked={restrictions[UserPermissionService.inventoryRestriction]}
        disabled={disabled}
        label={messages.inventoryRestriction}
        onChange={(value) => updateForm(UserPermissionService.inventoryRestriction, value)}
      />
      <div className={styles['restrict-workstation']}>
        <Restriction
          checked={isWorkstationApprovalRequired}
          disabled={disabled}
          label={messages.workstationApprovalRestriction}
          onChange={(value) => updateForm('isWorkstationApprovalRequired', value)}
        />
        <Button onClick={toggleLearnMore} theme='link'>
          <FormattedMessage {...messages.learnMoreHere} />
        </Button>
      </div>
      {isWorkstationApprovalRequired && (
        <div className={styles['workstation-approval']}>
          <div>{workstationStatusLabel}</div>
          <Button
            classnames={classnames({
              [styles.approve]: isWorkstationApprovalRequired,
              [styles.disapprove]: !isWorkstationApprovalRequired,
            })}
            onClick={onButtonClick}
            theme={THEME_CONSTANTS.tertiary}
          >
            {workstationApprovalButtonText}
          </Button>
        </div>
      )}
      {showLearnMore && (
        <div className={styles['learn-more']}>
          <div className={styles.header}>
            <FormattedMessage {...messages.workstationApprovalHeader} />
          </div>
          <div className={styles.section}>
            <FormattedMessage {...messages.workstationApprovalContent1} />
          </div>
          <div className={styles.section}>
            <FormattedMessage {...messages.workstationApprovalContent2} />
          </div>
          <div className={styles.section}>
            <FormattedMessage {...messages.workstationApprovalContent3} />
          </div>
          <div className={styles.section}>
            <Button onClick={toggleLearnMore} theme={THEME_CONSTANTS.tertiary}>
              <FormattedMessage {...messages.closeHelp} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

Restrictions.propTypes = {
  restrictions: PropTypes.shape({
    [UserPermissionService.orderViewRestriction]: PropTypes.bool,
    [UserPermissionService.shipmentViewRestriction]: PropTypes.bool,
    [UserPermissionService.inventoryRestriction]: PropTypes.bool,
  }),
  updateForm: PropTypes.func.isRequired,
  username: PropTypes.string,
  isWorkstationApprovalRequired: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default Restrictions;
